import { observer } from 'mobx-react-lite';
import { DefaultRootState } from 'react-redux';
import { Switch } from '@mantine/core';
import ProfileWithDropdownActions from '../../components/ProfileWithDropdownActions/ProfileWithDropdownActions';
import { getCurrentUserOwnsSingleWorkspace, getFirstWorkspaceInWorkspacesArray } from '../../selectors/workspaces';
import useNavigate from '../../new_arch/navigation/useNavigate';
import User from '../../new_arch/models/user/user';
import Workspace from '../../entities/workspace';
import useLogout from '../../new_arch/modules/authentication/queries/useLogout';
import { connectComponent } from '../../lib/connectComponent';
import { getCurrentUser } from '../../selectors/currentUser';
import { useIsGuest } from '../../hooks/useIsGuest';
import FreeAccountPushForGuests from '../../components/FreeAccountPushForGuests/FreeAccountPushForGuests';
import { useRootStore } from '../../new_arch/providers/RootStoreProvider';
import BetaBadge from '../../new_arch/modules/ui/components/BetaBadge/BetaBadge';

type Props = {
	defaultWorkspace: Workspace | undefined,
	userOwnsOneWorkspace: boolean,
	currentUser: User | undefined,
}

export const ProfileWithDropdownActionsContainer = observer( (
	{ defaultWorkspace, userOwnsOneWorkspace, currentUser } : Props
) => {
	const navigate = useNavigate();
	const { logout } = useLogout();
	const isGuest = useIsGuest();
	const { colorSchemeStore } = useRootStore();
	const isDarkMode = colorSchemeStore.colorScheme === 'dark';

	const badgeForWorkspaceStatus = {
		text: defaultWorkspace?.status.text,
		dangerStyle: !defaultWorkspace?.status.isActiveStatus
	};

	const onLogoutClicked = () => {
		// eslint-disable-next-line no-alert
		if ( window.confirm( 'Are you sure you want to log out?' ) ) {
			logout();
		}
	};

	const showsWorkspaceStatusBadge = userOwnsOneWorkspace && !!defaultWorkspace
			&& defaultWorkspace.status.showsInProfileDropdown;

	if ( !currentUser ) return null;

	const nameAndAvatar = {
		name: currentUser.name,
		description: currentUser?.email,
		image: currentUser.avatar,
		onClick: () => {},
		size: 20,
		color: 'var(--color-text-secondary)'
	};

	const guestDropdownActions = [
		nameAndAvatar,
		{
			...<FreeAccountPushForGuests />,
			classNames: 'free-account-push-item',
			onClick: () => {}
		}
	];

	const profileDropdownActions = [
		nameAndAvatar,
		{
			name: 'Settings',
			description: 'My account, Workspaces & Billing',
			icon: 'settings_outlined',
			onClick: navigate.toProfile,
			size: 20,
			color: 'var(--color-text-secondary)',
			badge: showsWorkspaceStatusBadge && badgeForWorkspaceStatus
		},
		{
			name: (
				<span className="appearance-name">
					Appearance
					<BetaBadge />
				</span>
			),
			description: 'Switch to dark mode',
			icon: 'dark-mode',
			size: 20,
			color: 'var(--color-text-secondary)',
			control: <Switch size="sm" checked={isDarkMode} onChange={colorSchemeStore.toggle} />
		},
		{
			name: 'Logout',
			icon: 'exit_to_app',
			onClick: onLogoutClicked,
			size: 20,
			color: 'var(--color-text-secondary)'
		}
	];

	const actions = isGuest ? guestDropdownActions : profileDropdownActions;

	return <ProfileWithDropdownActions actions={actions} currentUser={currentUser} isGuest={isGuest} />
} );

type ReduxSelector<R> = ( state: DefaultRootState, props: Props ) => R

const getDefaultWorkspace = getFirstWorkspaceInWorkspacesArray as ReduxSelector<Workspace | undefined>;
const getUserOwnsWorkspace = getCurrentUserOwnsSingleWorkspace as ReduxSelector<boolean>;

export default connectComponent( ( state: DefaultRootState, props: Props ) => ( {
	defaultWorkspace: getDefaultWorkspace( state, props ),
	userOwnsOneWorkspace: getUserOwnsWorkspace( state, props ),
	currentUser: getCurrentUser( state )
} ) )( ProfileWithDropdownActionsContainer );
